import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import Modal from './Modal';
import useCopy from '../util/useCopy';
import ObjectiveStateResource from '../resources/ObjectiveState';
import { Header } from './Header';
import { upperFirst } from 'lodash';
import { BallotPreviewItem } from './BallotPreviewItem';
import { Tooltip } from './Tooltip';
import ChevronDownIcon from './ChevronDownIcon';
import optionTypes from '../resources/Ballot/constants/optionTypes';
import events from '../resources/Ballot/constants/events';
import { oscarDatabase } from '../config/constants';
import PreviousYearResultOrderResource from '../resources/ObjectiveState/PreviousYearResultOrder';

let EVENT_ICON_SIZE = 16;

function softHyphenate(text: string) {
  if (text.includes(' ') || text.length <= 4) return text;
  let mid = Math.ceil(text.length / 2);
  return text.slice(0, mid) + '\u00AD' + text.slice(mid);
}

// TODO: remove after 2026 — Live Action Short tie override
let LIVE_ACTION_SHORT_EQUIVALENT_NOMINEES = new Set([
  'iTZ1eyNVdCrdPfyR2fpoh', // The Singers
  'rLEc89MBBOh2EGXj-dKwG', // Two People Exchanging Saliva
]);

export let ObjectiveStateResultsModal = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) => {
  let copy = useCopy();
  let objectiveState = ObjectiveStateResource.use();
  let previousYearResultOrder = PreviousYearResultOrderResource.use();
  let [showRemaining, setShowRemaining] = useState(false);

  let eventPointsMap = useMemo(() => {
    let map: {
      [eventName: string]: {
        type: string;
        section: string;
        displayName: string;
        points: number;
      }[];
    } = {};

    Object.entries(objectiveState.ballotShape).forEach(
      ([sectionKey, section]) => {
        if (!section || !section.options) return;
        Object.entries(section.options).forEach(([optionKey, option]) => {
          if (!option || !option.type || !option.data) return;
          if (
            option.type === optionTypes.eventCount.key ||
            option.type === optionTypes.eventBoolean.key
          ) {
            const eventName = option.data.eventName;
            const points = option.data.points || 0;

            if (!eventName) return;

            if (!map[eventName]) {
              map[eventName] = [];
            }

            map[eventName].push({
              type: option.type,
              section: sectionKey,
              displayName: section.displayName || sectionKey,
              points: points,
            });
          }
        });
      },
    );

    return map;
  }, [objectiveState]);

  let totalCategories = Object.keys(oscarDatabase.nomineeCategories).length;
  let announcedCategoryKeys = objectiveState.results.map(
    (item) => Object.keys(item)[0],
  );
  let remainingCategories = useMemo(() => {
    let allCategoryKeys = Object.keys(oscarDatabase.nomineeCategories);
    return allCategoryKeys
      .filter((k) => !announcedCategoryKeys.includes(k))
      .toSorted(
        (a, b) =>
          previousYearResultOrder.indexOf(a) -
          previousYearResultOrder.indexOf(b),
      )
      .map(
        (k) =>
          (
            oscarDatabase.nomineeCategories as Record<string, { title: string }>
          )[k]?.title,
      )
      .filter(Boolean);
  }, [announcedCategoryKeys, previousYearResultOrder]);
  let remainingCount = remainingCategories.length;
  let showRemainingIndicator =
    !objectiveState.didCeremonyEnd &&
    remainingCount > 0 &&
    remainingCount <= totalCategories / 2;

  return (
    <Modal isOpen={isOpen} onClose={onClose} disableBodyPadding>
      <div className="text-md relative flex flex-1 flex-col">
        <div className="flex-none bg-white">
          <Header className="mx-4 border-b-4 border-neutral-100 px-2 py-4">
            {upperFirst(copy.results)}
          </Header>
        </div>
        {objectiveState.didCeremonyStart && (
          <div className="mx-4 flex gap-2 py-2">
            {Object.keys(events)
              // @ts-expect-error
              .map((key: keyof typeof events) => {
                let { recurring, getTitle, getDescription } = events[key];
                let points = eventPointsMap[key];
                if (
                  recurring ||
                  (!recurring && Boolean(objectiveState.events[key]))
                ) {
                  return (
                    <div
                      key={key}
                      className="relative flex min-w-0 flex-1 flex-col items-end gap-1 rounded-xl bg-neutral-100 px-3 py-2 text-right"
                    >
                      <optionTypes.eventCount.Icon className="absolute top-2 left-3 text-neutral-400" style={{ width: EVENT_ICON_SIZE, height: EVENT_ICON_SIZE }} />
                      <Tooltip
                        text={getDescription(copy)}
                        tooltipSide="bottom"
                        className="cursor-pointer text-sm font-medium"
                      >
                        <span className="underline decoration-dotted">
                          {/* Spacer matching EVENT_ICON_SIZE to avoid overlapping the absolutely-positioned icon */}
                          <span className="inline-block" style={{ width: EVENT_ICON_SIZE }} />
                          {softHyphenate(getTitle(copy))}
                        </span>
                      </Tooltip>
                      <div className="text-md font-bold">
                        {points.map((point, index) => {
                          let pts = recurring
                            ? ((objectiveState.events[key] as number) || 0) *
                              point.points
                            : point.points;
                          return (
                            <div
                              key={index}
                              className={
                                pts > 0
                                  ? 'text-rose-default'
                                  : 'text-neutral-500'
                              }
                            >
                              {pts > 0 && '+ '} {pts}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                }
                return null;
              })}
          </div>
        )}
        {showRemainingIndicator && (
          <div
            className="mx-4 cursor-pointer rounded-xl bg-neutral-100 px-3 py-3"
            onClick={() => setShowRemaining((prev) => !prev)}
          >
            <div className="flex items-center justify-between text-sm font-medium text-neutral-500">
              <span>
                {remainingCount} {copy.categories} {copy.toGo}
              </span>
              <motion.span
                animate={{ rotate: showRemaining ? 180 : 0 }}
                transition={{ type: 'spring', stiffness: 300, damping: 25 }}
                className="flex items-center"
              >
                <ChevronDownIcon className="h-4 w-4 fill-neutral-400" />
              </motion.span>
            </div>
            <AnimatePresence initial={false}>
              {showRemaining && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                  className="overflow-hidden"
                >
                  <div className="mt-2 text-sm text-neutral-400">
                    {remainingCategories.map((title) => (
                      <div key={title} className="py-0.5">
                        {title}
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}
        {/* TODO: remove augmentedResults after 2026 — Live Action Short tie override */}
        {(() => {
          let augmentedResults = [...objectiveState.results];
          for (let i = 0; i < augmentedResults.length; i++) {
            let val = Object.values(augmentedResults[i])[0];
            if (LIVE_ACTION_SHORT_EQUIVALENT_NOMINEES.has(val as string)) {
              let catKey = Object.keys(augmentedResults[i])[0];
              let other = Array.from(
                LIVE_ACTION_SHORT_EQUIVALENT_NOMINEES,
              ).find((id) => id !== val);
              if (other) augmentedResults.splice(i + 1, 0, { [catKey]: other });
              break;
            }
          }
          return augmentedResults;
        })().map((result) => {
          let [key, value] = Object.entries(result)[0];
          let title = null;
          let selectedOption = null;

          for (let ballotSectionKey in objectiveState.ballotShape) {
            // @ts-expect-error
            for (let ballotSectionOptionKey in objectiveState.ballotShape[
              ballotSectionKey
            ].options) {
              if (
                // @ts-expect-error
                objectiveState.ballotShape[ballotSectionKey].options[
                  ballotSectionOptionKey
                ]?.data?.nomineeId === value
              ) {
                title =
                  // @ts-expect-error
                  objectiveState.ballotShape[ballotSectionKey].displayName;
                selectedOption =
                  // @ts-expect-error
                  objectiveState.ballotShape[ballotSectionKey].options[
                    ballotSectionOptionKey
                  ];
                break;
              }
            }
          }
          if (!value || !title) return null;
          // TODO: remove after 2026 — Live Action Short tie override
          let isTie = LIVE_ACTION_SHORT_EQUIVALENT_NOMINEES.has(
            value as string,
          );
          return (
            <div key={key + '-' + value} className="mx-4 mt-4">
              <BallotPreviewItem
                ballotItemDisplayName={isTie ? `${title} (Tie)` : title}
                selectedOption={selectedOption}
              />
            </div>
          );
        })}
      </div>
    </Modal>
  );
};
